import type { Messages } from '@blinkered/i18n'
import { useEffect, useState } from 'react'
import type { TieredIndex } from '@blinkered/words'
import { Avatar } from './Avatar.js'
import { PageHead } from './PageHead.js'
import { GameDetail } from './GameDetail.js'
import { ReportButton } from './ReportDialog.js'
import { GamesTable } from './GamesTable.js'
import { playerGames, playerProfile } from './account.js'
import type { PlayedGame, PublicProfile } from './account.js'
import { countryName } from './countries.js'
import { goTo } from './route.js'

/**
 * Somebody's profile, as anybody sees it.
 *
 * The same three facts the account screen shows about you -- the picture, the name, the country
 * and the bio -- and the same list of games, because there is no second version of what a game
 * is. What it never carries is the address the account signs in with: the server returns a
 * `PublicProfile` rather than a `Profile` for exactly that reason, and the two types stay
 * separate so that whatever a profile gains next is not public by default.
 *
 * A missing name and a deleted account read the same, deliberately. Telling them apart would
 * make this page report who used to be here.
 */
export function PlayerPage({
  messages,
  username,
  me,
  onSignIn,
  onHome,
}: {
  readonly messages: Messages
  readonly username: string
  /** Who is reading, or null. Decides only whether the report button is drawn; see `GameDetail`. */
  readonly me: string | null
  /** Opens the sign-in dialog, for a stranger who wants to report this profile. */
  readonly onSignIn: () => void
  readonly onHome: () => void
}): React.JSX.Element {
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [games, setGames] = useState<readonly PlayedGame[] | null>(null)
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    let live = true
    setProfile(null)
    setGames(null)
    setMissing(false)
    void playerProfile(username).then((found) => {
      if (!live) return
      if (found === null) {
        setMissing(true)
        return
      }
      setProfile(found)
      void playerGames(username).then((played) => {
        if (live) setGames(played ?? [])
      })
    })
    return () => {
      live = false
    }
  }, [username])

  if (missing) {
    return (
      <div className="account-page">
        <HomeLink messages={messages} onHome={onHome} />
        <p className="signin-note is-bad">{messages.playerNotFound}</p>
      </div>
    )
  }
  if (profile === null) {
    return (
      <div className="account-page">
        <HomeLink messages={messages} onHome={onHome} />
        <p className="dim">{messages.profileLoading}</p>
      </div>
    )
  }

  return (
    <div className="account-page">
      <HomeLink messages={messages} onHome={onHome} />
      <header className="account-head">
        <Avatar seed={profile.avatarSeed} size={56} className="avatar-large" />
        <div>
          <h1 className="account-name">{profile.username}</h1>
          {profile.country === null ? null : (
            <p className="dim">{countryName(profile.country, 'en')}</p>
          )}
        </div>
        {/*
          The two free-text surfaces a person has, reported from the page that shows them both.
          
          Never on your own page: the server refuses a self-report and a control that exists only
          to be refused is worse than no control.
        */}
        {me === profile.userId ? null : (
          <ReportButton
            messages={messages}
            subject={{ kind: 'person', username: profile.username }}
            signedIn={me !== null}
            onSignIn={onSignIn}
          />
        )}
      </header>
      {/* Text, and only ever text. React escapes by default and nothing here parses the string
          looking for links; see docs/ACCOUNTS.md on why the bio is never markup. */}
      {profile.bio === null ? null : <p className="player-bio">{profile.bio}</p>}

      <h2>{messages.gamesHeading}</h2>
      {games === null ? (
        <p className="dim">{messages.theirGamesLoading}</p>
      ) : games.length === 0 ? (
        <p className="dim">{messages.gamesEmptyShort}</p>
      ) : (
        <GamesTable messages={messages} games={games} />
      )}
    </div>
  )
}

/**
 * One player's game, reached by its permalink.
 *
 * Thin on purpose: the detail view is the same component the account screen uses, because a
 * game is one thing and a second renderer for the public copy would eventually disagree with
 * the private one about what a round was.
 */
export function PlayedGamePage({
  id,
  messages,
  me,
  onSignIn,
  dictionary,
  onHome,
}: {
  readonly id: string
  readonly messages: Messages
  readonly me: string | null
  /** Opens the sign-in dialog, for a stranger who wants to report the score. */
  readonly onSignIn: () => void
  readonly dictionary: TieredIndex | null
  readonly onHome: () => void
}): React.JSX.Element {
  return (
    <div className="account-page">
      <HomeLink messages={messages} onHome={onHome} />
      <GameDetail
        messages={messages}
        id={id}
        me={me}
        onSignIn={onSignIn}
        dictionary={dictionary}
        onBack={undefined}
      />
    </div>
  )
}

/**
 * The way home, which is the shared head now.
 *
 * It used to be a bare link reading "← Play Blinkered" in English on every one of the fifty-one,
 * which is the sort of thing that survives because nobody reading English ever sees it.
 */
function HomeLink({
  messages,
  onHome,
}: {
  readonly messages: Messages
  readonly onHome: () => void
}): React.JSX.Element {
  return (
    <PageHead
      back={messages.backToGame}
      onHome={() => {
        goTo({ at: 'game' })
        onHome()
      }}
    />
  )
}
